<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M23 7l-7 5 7 5V7z" stroke="#ff9600" fill="#ff9600" fill-opacity="0.2"/>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="#ff9600" fill="#ff9600" fill-opacity="0.1"/>
            </svg>
          </div>
          <h1 class="brand-title">灵眸云图</h1>
          <p class="brand-desc">精彩直播，尽在灵眸云图</p>
        </div>
        <div class="login-form">
          <h2 class="form-title">欢迎回来</h2>
          <p class="form-subtitle">登录您的账号，继续精彩之旅</p>
          <div class="form-group">
            <div class="input-wrapper">
              <svg class="input-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#999" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/></svg>
              <input type="text" v-model="account" placeholder="请输入账号" @keyup.enter="handleLogin">
            </div>
          </div>
          <div class="form-group">
            <div class="input-wrapper">
              <svg class="input-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#999" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              <input type="password" v-model="password" placeholder="请输入密码" @keyup.enter="handleLogin">
            </div>
          </div>
          <button class="login-btn" @click="handleLogin" :disabled="loading">
            <span v-if="loading" class="loading-spinner"></span>
            <span>{{ loading ? '登录中...' : '登录' }}</span>
          </button>
          <div class="form-footer">
            <router-link to="/register" class="register-link">还没有账号？立即注册</router-link>
          </div>
          <div class="minor-notice">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z"/>
            </svg>
            <span>若本机由未成年人使用，建议登录后开启青少年模式（含时长限制、宵禁与消费保护）；完成实名认证后按认证结果自动适配。</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '../stores/user';
import { login } from '../api/user';

export default {
  name: 'UserLogin',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const userStore = useUserStore();
    const account = ref('');
    const password = ref('');
    const loading = ref(false);

    const handleLogin = async () => {
      if (!account.value || !password.value) {
        alert('请输入账号和密码');
        return;
      }
      // 账号格式校验：手机号或邮箱或 3-30 位字母数字下划线
      const acct = account.value.trim();
      const isMobile = /^1[3-9]\d{9}$/.test(acct);
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(acct);
      const isUsername = /^[A-Za-z0-9_]{3,30}$/.test(acct);
      if (!isMobile && !isEmail && !isUsername) {
        alert('账号格式不正确');
        return;
      }
      if (password.value.length < 6 || password.value.length > 30) {
        alert('密码长度需在 6-30 个字符之间');
        return;
      }
      loading.value = true;
      try {
        const response = await login(account.value, password.value);
        if (response.data.code === 1) {
          userStore.setUserInfo(response.data.data.userinfo);
          const redirect = typeof route.query.redirect === 'string' && route.query.redirect.startsWith('/')
            ? route.query.redirect
            : '/';
          router.replace(redirect);
        } else {
          alert(response.data.msg || '登录失败');
        }
      } catch (error) {
        console.error('登录失败:', error);
        alert(error?.message || '登录失败，请稍后重试');
      } finally {
        loading.value = false;
      }
    };

    return { account, password, loading, handleLogin };
  }
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
  position: relative;
  overflow: hidden;
}

.login-page::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -30%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(255,150,0,0.08) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.login-page::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -20%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(255,106,0,0.06) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.login-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.login-card {
  display: flex;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 24px 80px rgba(0,0,0,0.3);
  overflow: hidden;
  width: 800px;
  max-width: 92vw;
  min-height: 480px;
}

.login-brand {
  width: 320px;
  background: linear-gradient(135deg, #ff6a00, #ff9600, #ffaa33);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.login-brand::before {
  content: '';
  position: absolute;
  top: -100px;
  right: -100px;
  width: 300px;
  height: 300px;
  background: rgba(255,255,255,0.08);
  border-radius: 50%;
}

.login-brand::after {
  content: '';
  position: absolute;
  bottom: -60px;
  left: -60px;
  width: 200px;
  height: 200px;
  background: rgba(255,255,255,0.06);
  border-radius: 50%;
}

.brand-icon {
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.brand-title {
  font-size: 32px;
  font-weight: 800;
  margin: 0 0 8px;
  letter-spacing: 2px;
  position: relative;
  z-index: 1;
}

.brand-desc {
  font-size: 15px;
  opacity: 0.85;
  position: relative;
  z-index: 1;
  margin: 0;
}

.login-form {
  flex: 1;
  padding: 48px 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.form-title {
  font-size: 26px;
  color: #1a1a2e;
  font-weight: 700;
  margin: 0 0 6px;
}

.form-subtitle {
  font-size: 14px;
  color: #94a3b8;
  margin: 0 0 32px;
}

.form-group {
  margin-bottom: 20px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  pointer-events: none;
  flex-shrink: 0;
}

.input-wrapper input {
  width: 100%;
  padding: 14px 14px 14px 44px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  color: #1e293b;
  background: #f8fafc;
  outline: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.input-wrapper input:focus {
  border-color: #ff9600;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(255,150,0,0.1);
}

.input-wrapper input::placeholder {
  color: #94a3b8;
}

.login-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  box-shadow: 0 4px 16px rgba(255,106,0,0.3);
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(255,106,0,0.4);
}

.login-btn:active:not(:disabled) {
  transform: translateY(0);
}

.login-btn:disabled {
  background: #e2e8f0;
  box-shadow: none;
  cursor: not-allowed;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-footer {
  text-align: center;
  margin-top: 24px;
}

.register-link {
  color: #ff9600;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.register-link:hover {
  color: #ff6a00;
  text-decoration: underline;
}

.minor-notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 18px;
  padding: 10px 12px;
  background: #e9f7f0;
  border: 1px solid #cfe9dd;
  border-radius: 10px;
  color: #1f7a5a;
  font-size: 12px;
  line-height: 1.6;
}
.minor-notice svg {
  flex: 0 0 auto;
  margin-top: 2px;
}

@media (max-width: 768px) {
  .login-card {
    flex-direction: column;
    min-height: auto;
  }
  .login-brand {
    width: 100%;
    padding: 32px;
    min-height: 160px;
  }
  .login-form {
    padding: 32px 28px;
  }
  .brand-title {
    font-size: 26px;
  }
}
</style>
